import '@/styles/application.css'
import 'react-datepicker/dist/react-datepicker.css'
import { createRoot } from 'react-dom/client'
import { createInertiaApp, router } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { AdminLayout } from '@/components/AdminLayout'
import { AgencyLayout } from '@/components/AgencyLayout'
import { toast } from 'sonner'
import React from 'react'
import * as Sentry from '@sentry/react'

// Sentry 初期化
// DSN は Rails 側で layout の meta タグに埋め込まれる (環境変数 SENTRY_DSN_JS)
// 未設定の環境では Sentry.init を呼ばないので副作用なし
function getMeta(name: string): string | null {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null
}

const sentryDsn = getMeta('sentry-dsn')
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: getMeta('sentry-environment') ?? 'production',
    // 動的インポート失敗時の自動リロードで発生するノイズは除外
    ignoreErrors: [
      /Failed to fetch dynamically imported module/,
      /Loading chunk \d+ failed/,
      /Loading CSS chunk/,
      /Importing a module script failed/,
    ],
    // エラー監視のみ利用 (APM/セッションリプレイは送らない)
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  })
}

// 動的インポート失敗時の自動リロード（デプロイ後のアセット不整合対策）
// リロードループ防止のため、短時間での連続リロードを制限
const RELOAD_COOLDOWN_KEY = 'asset_reload_timestamp'
const RELOAD_COOLDOWN_MS = 10000 // 10秒以内の連続リロードを防止

function shouldAutoReload(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_COOLDOWN_KEY)
  if (lastReload) {
    const elapsed = Date.now() - parseInt(lastReload, 10)
    if (elapsed < RELOAD_COOLDOWN_MS) {
      console.warn('[AssetReload] Skipping reload - cooldown active')
      return false
    }
  }
  return true
}

function performAssetReload(): void {
  if (!shouldAutoReload()) {
    toast.error('ページの読み込みに問題が発生しました。ブラウザを更新してください。')
    return
  }
  
  console.log('[AssetReload] New assets detected. Reloading page...')
  sessionStorage.setItem(RELOAD_COOLDOWN_KEY, Date.now().toString())
  window.location.reload()
}

// グローバルエラーハンドラー：動的インポート失敗をキャッチ
window.addEventListener('error', (event) => {
  // 動的インポート失敗のエラーメッセージをチェック
  if (event.message?.includes('Failed to fetch dynamically imported module') ||
      event.message?.includes('Loading chunk') ||
      event.message?.includes('Loading CSS chunk')) {
    event.preventDefault()
    performAssetReload()
  }
})

// Promiseの未処理リジェクションもキャッチ（動的インポートはPromiseベース）
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || String(event.reason)
  if (reason?.includes('Failed to fetch dynamically imported module') ||
      reason?.includes('Loading chunk') ||
      reason?.includes('Loading CSS chunk')) {
    event.preventDefault()
    performAssetReload()
  }
})

// Inertia.jsのグローバルエラーハンドリング
router.on('error', (event) => {
  const response = (event.detail as any).response

  // 409 Conflict = アセットバージョン不一致（デプロイ後）
  // → 古いJSファイルが見つからない場合は自動リロード
  if (response?.status === 409) {
    console.log('[Inertia] Asset version mismatch detected. Reloading...')

    // 遷移先URLを保存してリロード後に復元
    const targetUrl = (event.detail as any).page?.url || window.location.pathname
    sessionStorage.setItem('inertia_reload_redirect', targetUrl)

    performAssetReload()
    return
  }

  event.preventDefault() // デフォルトのモーダルを抑制

  // エラーオブジェクトからメッセージを取得
  const errors = (event.detail as any).errors
  if (errors && typeof errors === 'object') {
    const firstError = Object.values(errors)[0]
    if (firstError) {
      const errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError)
      toast.error(errorMessage)
      return
    }
  }

  // 403 や 401 など、Inertia がフィールド errors を埋めない非バリデーション
  // 系のエラーは response body の errors / error から拾って表示
  const responseData = response?.data
  if (responseData) {
    if (Array.isArray(responseData.errors) && responseData.errors[0]) {
      toast.error(String(responseData.errors[0]))
      return
    }
    if (typeof responseData.error === 'string' && responseData.error) {
      toast.error(responseData.error)
      return
    }
  }
})

// リロード後の遷移先復元
router.on('navigate', () => {
  const redirectUrl = sessionStorage.getItem('inertia_reload_redirect')
  if (redirectUrl && redirectUrl !== window.location.pathname) {
    sessionStorage.removeItem('inertia_reload_redirect')
    router.visit(redirectUrl)
  }
})

// ページ遷移成功時にエラープロップとflashメッセージをチェック
router.on('success', (event) => {
  const page = (event.detail as any).page

  // props.errorがあればToastで表示
  if (page && page.props && page.props.error) {
    toast.error(String(page.props.error))
  }

  // props.errorsオブジェクトもチェック
  if (page && page.props && page.props.errors) {
    const errors = page.props.errors
    if (typeof errors === 'object' && Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]
      if (firstError) {
        const errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError)
        toast.error(errorMessage)
      }
    }
  }

  // Railsのflashメッセージを自動的にToastで表示
  if (page && page.props && page.props.flash) {
    const flash = page.props.flash

    // flash.notice（成功メッセージ）
    if (flash.notice) {
      toast.success(String(flash.notice))
    }

    // flash.alert（警告メッセージ）
    if (flash.alert) {
      toast.warning(String(flash.alert))
    }

    // flash.error（エラーメッセージ）
    if (flash.error) {
      toast.error(String(flash.error))
    }

    // flash.success（成功メッセージ）
    if (flash.success) {
      toast.success(String(flash.success))
    }
  }
})

createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob('../pages/**/*.tsx', { eager: false })
    const component = await pages[`../pages/${name}.tsx`]()
    const page = (component as any).default

    // Layoutが不要なページ（Auth、NotFoundなど）を除外
    const noLayoutPages = [
      'Auth/Login',
      'Auth/Register',
      'users/NotFound',
      'FriendAddQr',
      'FriendComplete',
      'LiffApp',
      'public/SurveyShow',
      'admins/Login',
      'share/ScenarioStepLocked',
      'share/ScenarioStepShow',
      'share/ScenarioStepUnavailable',
      'share/ScenarioShare',
      'share/SharePassword',
      'share/ShareInvalid',
      'agencies/Login',
      'agencies/Register',
      'agency_invitations/Show',
      'agency_invitations/Invalid'
    ]

    if (noLayoutPages.includes(name)) {
      return page
    }

    // Admin用ページはAdminLayoutを使用
    if (name.startsWith('admins/')) {
      page.layout = (pageContent: React.ReactNode) => <AdminLayout>{pageContent}</AdminLayout>
      return page
    }

    // Agency用ページは専用レイアウト (admin サイドバーは出さない)
    if (name.startsWith('agencies/')) {
      page.layout = (pageContent: React.ReactNode) => <AgencyLayout>{pageContent}</AgencyLayout>
      return page
    }

    // デフォルトでLayoutをラップ
    page.layout = (pageContent: React.ReactNode) => <Layout>{pageContent}</Layout>
    return page
  },
  setup({ el, App, props }) {
    createRoot(el).render(<App {...props} />)
  },
})
